import moment from "moment";
import { Viewerlayer } from "./viewerlayer";
import { IProjectAOI } from "../interfaces/i-project-aoi";
import { IMetadataImage } from "../interfaces/i-metadataimage";
import { ImagesService } from "../services/internal-services/images.service";
import { ViewerConstants } from "../../shared/model/viewer-constants";

const isWide = (layer: Viewerlayer): boolean => {
  return (
    (layer?.collectionType === ViewerConstants.COLLECTION_PREFIX.BLUESKY &&
      layer?.layer?.toLowerCase().includes("wide")) ||
    layer?.layer?.toLowerCase().includes("high")
  );
};
const isGray = (layer: Viewerlayer): boolean => {
  return layer?.collectionType === ViewerConstants.COLLECTION_PREFIX.GRAYSKY;
};

export const ProjectAOI: (
  name: string,
  prettyName: string,
  layer: Viewerlayer,
  minDate: Date,
  maxDate: Date,
  maxZoom: number,
  averageGsd: number,
  aoiOblique: boolean,
  nadirDate: Date,
  collectionName?: string,
  collectionType?: string,
  orientations?: string[],
  images?: IMetadataImage[],
  enabled?: boolean,
  geometry?: any,
) => IProjectAOI = (
  name: string,
  prettyName: string,
  layer: Viewerlayer,
  minDate: Date,
  maxDate: Date,
  maxZoom: number,
  averageGsd: number,
  aoiOblique: boolean,
  newestNadirDate: Date,
  collectionName?: string,
  collectionType?: string,
  orientations?: string[],
  images?: IMetadataImage[],
  enabled?: boolean,
  geometry?: any,
) => ({
  name,
  layer,
  minDate,
  maxDate,
  maxZoom,
  averageGsd,
  aoiOblique,
  nadirDate: newestNadirDate,
  collectionName,
  collectionType,
  orientations,
  images,
  enabled,
  geometry,
  hasChilds: () => {
    return !!layer.aois && !!layer.aois.length;
  },
  getLayerName: () => {
    return layer?.name || null;
  },
  getSourceLayer: () => {
    return layer?.name || null;
  },
  getName: () => {
    return name;
  },
  getParentAOI: () => {
    return layer?.parent_aoi || null;
  },
  hasOrientation: (orientation) => {
    return orientations.includes(orientation);
  },
  hasNonArchivedOrientation: (orientation) => {
    return images
      ? images.some((img) => ImagesService.normalizeOrientation(img.orientation) === orientation && !img.archived)
      : false;
  },
  hasInfrared: () => {
    return layer.enableInfrared;
  },
  isEnabled: () => {
    return enabled || false;
  },
  isEnabled3D: () => {
    return layer?.enableOblique || false;
  },
  isAOIOblique: () => {
    return aoiOblique;
  },
  hasObliques: () => {
    return orientations?.some((o) => ViewerConstants.OBLIQUE_ORIENTATIONS.includes(o));
  },
  enabledContrast: () => {
    return layer.enableContrast;
  },
  isVisible: () => {
    // // is child aoi
    // if (!!layer.parent_id && layer.aoi) {
    //   return layer.collection_visible && layer.parent_visible && layer.visible;
    // }
    // is aoi
    //Need this????
    // if (layer.aoi) {
    //   return layer.collection_visible && layer.visible;
    // }
    // is collection
    return layer.visible || false;
  },
  isParentVisible: () => {
    return layer?.parent_visible || false;
  },
  isCollectionVisible: () => {
    return layer?.collection_visible || false;
  },
  isGraySky: () => {
    return isGray(layer);
  },
  isWide: () => {
    return isWide(layer);
  },
  isUrban: () => {
    return (
      !isWide(layer) &&
      layer?.collectionType === ViewerConstants.COLLECTION_PREFIX.BLUESKY &&
      (layer?.layer?.toLowerCase().indexOf("urban") !== -1 || layer?.layer?.toLowerCase().indexOf("bluesky") !== -1)
    );
  },
  isUrbanR: () => {
    return layer?.layer?.toLowerCase().indexOf("urban-r") !== -1;
  },
  isLegacy: () => {
    return layer?.layer?.toLowerCase().indexOf("-g") !== -1;
  },
  getOrder: () => {
    return layer?.order ?? null;
  },
  getGroupOrder: () => {
    return layer?.group_order ?? null;
  },
  getParentGroupOrder: () => {
    return layer?.parent_group_order ?? null;
  },
  getAvgGsd: () => {
    return averageGsd || null;
  },
  getCoverageType: () => {
    return layer?.coverage_type || null;
  },
  isChildAOI: () => {
    return !!layer?.parent_id || false;
  },
  isAOI: () => {
    return !!layer?.aoi || false;
  },
  getLayerId: () => {
    return layer?.id?.toString() || null;
  },
  getCollectionId: () => {
    return layer?.collection_id?.toString() || null;
  },
  getCollectionType: () => {
    return layer?.collectionType || null;
  },
  getParentId: () => {
    return layer?.parent_id?.toString() || null;
  },
  getDescription: () => {
    return layer?.description || null;
  },
  getLayer: () => {
    return layer?.layer || null;
  },
  hasImage: (imageId: string) => {
    return images && images.findIndex((img) => img.image_id === imageId) !== -1;
  },
  hasImages: () => {
    return !!images?.length;
  },
  getNewestImage: (orientation: string = null) => {
    if (!images || !images.length) {
      return null;
    }
    let filtered = orientation
      ? images.filter((img) => ImagesService.normalizeOrientation(img.orientation) === orientation)
      : images;

    filtered = filtered.sort(
      (i1: IMetadataImage, i2: IMetadataImage) =>
        ImagesService.getCaptureDate(i2).getTime() - ImagesService.getCaptureDate(i1).getTime(),
    );
    return filtered[0] || null;
  },
  getPrettyDate: () => {
    if (newestNadirDate) {
      return `${moment(newestNadirDate).format("MMM DD, YYYY")}`;
    }
    const minMonth = maxDate ? moment(minDate).format("MMM") : "";
    const maxMonth = maxDate ? moment(maxDate).format("MMM") : "";
    if (layer.layer.indexOf("graysky") !== -1) {
      return `${minMonth !== maxMonth ? `${minMonth} - ` : ""} ${`${maxMonth} ${moment(maxDate).format("DD, YYYY")}`}`;
    }
    return `${minMonth !== maxMonth ? `${minMonth} - ` : ""} ${`${maxMonth} ${moment(maxDate).format("YYYY")}`}`;
  },
  getYearShortFormat: () => {
    const baseDate = newestNadirDate ?? maxDate; // ambos deberían ser Date
    return `\'${moment(baseDate).format("YY")}`;
  },
  getPrettyCollectionName: () => {
    return layer?.parent_pretty_name || collectionName;
  },
  getPrettyName: () => {
    return layer?.pretty_name || prettyName || name;
  },
  getParentPrettyName: () => {
    return layer?.parent_pretty_name || layer?.parent_aoi;
  },
  getMaxDate: (format: string = "MMM DD, YYYY") => {
    return `${moment(maxDate).format(format)}`;
  },
  getMaxZoom: () => {
    return maxZoom;
  },
  getYear: () => {
    const baseDate = newestNadirDate ?? maxDate; // ambos deberían ser Date
    return `${moment(baseDate).format("YYYY")}`;
  },
  getProject: () => {
    if (!!layer?.projects) return layer.projects.find((p) => p.children.find((c) => c.code == name))?.code;
  },
  allowDamageAssessment: () => {
    const year = moment(maxDate).format("YYYY");
    const isAfter2022 = year ? Number(year) >= ViewerConstants.DAMAGE_ASSESSMENT_MIN_YEAR : false;
    return isGray(layer) && isAfter2022;
  },
  setVisible: (value: boolean) => {
    layer.visible = value;
  },
  isCirAvailable: (is3D: boolean) => {
    return images
      ? images.some((img) => img.orientation === ViewerConstants.DISCRETE_ORIENTATIONS.I && (!is3D || !img.archived))
      : false;
  },
});
